import cropperConfig from '@/data/imageCropperSpecs.json'

/**
 * Resolved props consumed by <ui-image-cropper>. Spread with v-bind:
 *   <ui-image-cropper v-bind="cropperSpec('banner')" />
 */
export interface ICropperSpec {
  aspectRatio?: number
  minWidth?: number
  minHeight?: number
  /** px — longest edge the cropped result is scaled down to. */
  outputSize?: number
  outputWidth?: number
  outputHeight?: number
  recommended?: string
  /** MB — hard cap on the cropped file's size; unset means no cap. */
  maxSizeMB?: number
  accept?: string
  download?: boolean
  isMultiple?: boolean
}

interface IRawCropperSpec {
  ratio?: string
  /** px — longest edge of the smallest crop the user may select. */
  min?: number
  /** px — longest edge of the delivered image. */
  output?: number
  recommended?: string
  maxSizeMB?: number
  accept?: string
  download?: boolean
  isMultiple?: boolean
}

const ratios: Record<string, number[]> = cropperConfig.ratios
const specs: Record<string, IRawCropperSpec> = cropperConfig.specs

export type AspectRatioKey = keyof typeof cropperConfig.ratios
export type CropperSpecKey = keyof typeof cropperConfig.specs

const resolveRatio = (key?: string): number | undefined => {
  const tuple = key ? ratios[key] : undefined
  if (!tuple || tuple.length < 2) return undefined
  return tuple[0]! / tuple[1]!
}

/**
 * Expand a single "longest edge" value into both sides using the aspect ratio.
 * Sizes are configured as one number so they can never contradict the ratio.
 */
const resolveSides = (value?: number, aspectRatio?: number) => {
  if (!value) return undefined
  if (aspectRatio === undefined) return { width: value, height: value }
  return aspectRatio >= 1
    ? { width: value, height: Math.round(value / aspectRatio) }
    : { width: Math.round(value * aspectRatio), height: value }
}

/** Universal aspect-ratio presets (width / height) — e.g. AspectRatio.wide === 16 / 9. */
export const AspectRatio = Object.fromEntries(
  Object.entries(ratios).map(([key, value]) => [key, value[0]! / value[1]!]),
) as Record<AspectRatioKey, number>

/**
 * Resolve a named image-cropper spec from the global config into component props.
 * Edit src/data/imageCropperSpecs.json to manage ratios and size constraints.
 */
export const cropperSpec = (key: string): ICropperSpec => {
  const { ratio, min, output, recommended, ...rest } = specs[key] ?? {}
  const aspectRatio = resolveRatio(ratio)
  const minSides = resolveSides(min, aspectRatio)
  const outputSides = resolveSides(output, aspectRatio)

  return {
    ...rest,
    ...(aspectRatio === undefined ? {} : { aspectRatio }),
    ...(minSides ? { minWidth: minSides.width, minHeight: minSides.height } : {}),
    ...(outputSides
      ? { outputSize: output, outputWidth: outputSides.width, outputHeight: outputSides.height }
      : {}),
    // A derived hint only reads correctly when the ratio pins both sides down;
    // free-form crops keep whatever hint the spec spells out, or none at all.
    ...(recommended
      ? { recommended }
      : outputSides && aspectRatio !== undefined
        ? { recommended: `${outputSides.width} × ${outputSides.height} px` }
        : {}),
  }
}
